import { observer } from "mobx-react-lite";
import { useContext, useEffect, useState } from "react";
import { useField } from "react-final-form";

import { DateTime, TIME_FORMATS } from "@bps/date-time";
import { Spinner, Stack, Text } from "@bps/fluent-ui";
import { AppointmentFormContext } from "@modules/booking/screens/booking-calendar/components/appointment-dialog/components/appointment-form/context/AppointmentFormContext.ts";
import { useStores } from "@stores/hooks/useStores.ts";
import { DataFetcher } from "@ui-components/data-fetcher/DataFetcher.tsx";
import { DatePickerArrows } from "@ui-components/pickers/DatePickerArrows.tsx";
import { LocationOption } from "@ui-components/selects/LocationOption.tsx";

import {
  appointmentFormNameOf,
  GroupApptSlot,
  GroupApptSlots
} from "../AppointmentForm.types.ts";
import { EmptyState } from "../next-available-fields/EmptyState.tsx";
import { ProviderTimeSlotsHeader } from "../next-available-fields/ProviderTimeSlotsHeader.tsx";
import { AppointmentSlot } from "./AppointmentSlot.tsx";
import { appointmentSlotsGridStyles } from "./NextGroupAppts.styles.ts";

interface ProviderAppointmentsProps {
  pickerStartDate: Date;
  providerAppointments: GroupApptSlots;
}

export const ProviderAppointments: React.FunctionComponent<ProviderAppointmentsProps> =
  observer(({ pickerStartDate, providerAppointments }) => {
    const { core, practice } = useStores();

    const {
      selectedProviders,
      getGroupApptToggleText,
      getNextAvailableDate,
      getUpcomingGroupAppointments
    } = useContext(AppointmentFormContext);

    const [sliderStartDate, setSliderStartDate] = useState<Date | undefined>(
      pickerStartDate
    );

    const [_isExpanded, setIsExpanded] = useState(false);
    const [appointmentTimes, setAppointmentTimes] = useState<GroupApptSlot[]>(
      providerAppointments.appointmentTimes
    );

    useEffect(() => {
      setSliderStartDate(pickerStartDate);
    }, [pickerStartDate]);

    const {
      input: { value: appointmentType }
    } = useField<string>(appointmentFormNameOf("appointmentTypeId"), {
      subscription: { value: true }
    });

    const isSingleProvider = selectedProviders.length === 1;
    const isExpanded = _isExpanded || isSingleProvider;
    const minDate = DateTime.today().toJSDate();

    const changeDate = async (date: Date | undefined) => {
      setSliderStartDate(date);
      if (!date) return;
      if (appointmentType) {
        const newTimeSlots = await getUpcomingGroupAppointments(
          date,
          [providerAppointments.providerId],
          appointmentType
        );
        setAppointmentTimes(newTimeSlots[0].appointmentTimes);
      }
    };

    const apptGroupsByLocationsMap = new Map<string, GroupApptSlot[]>();
    appointmentTimes.forEach(groupAppt => {
      const arr = apptGroupsByLocationsMap.get(groupAppt.orgUnitId);
      if (!arr) {
        apptGroupsByLocationsMap.set(groupAppt.orgUnitId, [groupAppt]);
      } else {
        arr.push(groupAppt);
      }
    });

    const apptGroupsByLocations = Array.from(apptGroupsByLocationsMap.values());

    return (
      <Stack.Item
        styles={(_props, theme) => ({
          root: {
            borderBottomStyle: "solid",
            borderBottomColor: theme.palette.neutralLighter,
            borderBottomWidth: 1,
            paddingBottom: 16
          }
        })}
      >
        <Stack
          grow
          verticalAlign="center"
          tokens={{ childrenGap: 8 }}
          styles={{ root: { marginTop: 8, marginRight: 8 } }}
        >
          <ProviderTimeSlotsHeader
            providerId={providerAppointments.providerId}
            moreText={getGroupApptToggleText(appointmentTimes)}
            toggleIsExpanded={() => setIsExpanded(value => !value)}
            isExpanded={isExpanded}
          />

          <Stack verticalAlign="center" horizontalAlign="center">
            {isExpanded && (
              <DatePickerArrows
                calendarProps={{
                  minDate,
                  calendarMonthProps: {
                    onNavigateDate: changeDate
                  },
                  calendarDayProps: {
                    onNavigateDate: changeDate
                  }
                }}
                value={sliderStartDate}
                onChange={changeDate}
              />
            )}

            {sliderStartDate && (
              <Stack
                verticalFill
                horizontalAlign="center"
                verticalAlign={providerAppointments ? "start" : "center"}
                tokens={{ childrenGap: 8 }}
              >
                {providerAppointments.providerId &&
                appointmentTimes.length > 0 ? (
                  <>
                    <Text styles={{ root: { margin: 8 } }}>
                      Existing appointments
                    </Text>

                    {apptGroupsByLocations.map(locationGroup => {
                      const location = practice.pracOrgUnitMap.get(
                        locationGroup[0].orgUnitId
                      );
                      return (
                        <Stack key={locationGroup[0].orgUnitId}>
                          {core.hasMultipleActiveLocations && (
                            <LocationOption
                              color={
                                location?.orgUnitLocationData
                                  ?.appointmentBookMarkerCode
                              }
                              text={
                                core.getLocationName(
                                  locationGroup[0].orgUnitId
                                ) ?? ""
                              }
                            />
                          )}
                          <div style={appointmentSlotsGridStyles}>
                            {locationGroup.map((appointment: GroupApptSlot) => {
                              return (
                                <AppointmentSlot
                                  providerId={providerAppointments.providerId}
                                  pickerStartDate={pickerStartDate}
                                  sliderStartDate={sliderStartDate}
                                  isExpanded={isExpanded}
                                  slot={appointment}
                                  key={`${providerAppointments.providerId}-${appointment.startTime}`}
                                />
                              );
                            })}
                          </div>
                        </Stack>
                      );
                    })}
                  </>
                ) : (
                  <DataFetcher
                    refetchId={
                      providerAppointments.providerId + sliderStartDate
                    }
                    fetch={() =>
                      getNextAvailableDate(
                        providerAppointments.providerId,
                        sliderStartDate,
                        appointmentType
                      )
                    }
                    fallback={<Spinner />}
                  >
                    {nextAvailableAppointmentDate => {
                      const nextAvailableText = nextAvailableAppointmentDate
                        ? `Next available appointment is at ${nextAvailableAppointmentDate.toFormat(
                            TIME_FORMATS.DEFAULT_TIME_FORMAT
                          )}`
                        : "There are no available times for this provider.";
                      return (
                        <EmptyState
                          changeDate={changeDate}
                          setIsExpanded={setIsExpanded}
                          nextAvailableLinkText={nextAvailableAppointmentDate}
                          nextAvailableText={nextAvailableText}
                        />
                      );
                    }}
                  </DataFetcher>
                )}
              </Stack>
            )}
          </Stack>
        </Stack>
      </Stack.Item>
    );
  });
